import { Skeleton, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { IconButton, SkeletonText } from 'utoniq-ui'

import {
  ResaleCreateQuery,
  UserItemSerialDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import {
  ExhibitionContent,
  ExhibitionContentSkeleton,
  ExhibitionPeriod,
  ExhibitionPeriodSkeleton,
} from '@/components/organisms'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { encodeSkuId, getItemSkuPrice } from '@/core/sku'
import { getHelpUrl } from '@/lib/helpUrl'

import { TicketQuantityForm } from './TicketQuantityForm'

export * from './fragments'

export type State = {
  itemId: string
  skuId: string | undefined
  loading: boolean
  item: ResaleCreateQuery['item']
  unusedCount: number
  serials: UserItemSerialDetailFragment[]
  isResaleAvailable: boolean | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const ResaleCreateTemplate: Template<State, Action> = ({ state }) => {
  const { t } = useTranslation('resaleCreate')
  const { onClick: onClickHelp } = useBrowserLink(getHelpUrl('resaleExhibit'))
  const { generatePath, generateQueryPath, routerPages, router, routerNames } =
    useRouter()

  const { price, formattedPrice } = useMemo(
    () => getItemSkuPrice(state.item),
    [state.item]
  )
  return (
    <BaseLayout
      margin
      header={{
        back: true,
        title: t('title'),
        defaultBackPage: generateQueryPath(
          routerPages.OwnedItemDetail,
          { itemId: state.itemId ?? '#' },
          { skuId: encodeSkuId(state.item?.sku?.id ?? '') }
        ),
        right: (
          <IconButton.Help edge="end" onClick={onClickHelp} color="primary" />
        ),
      }}
    >
      <Head title={routerNames.ResaleCreate} />
      {state.loading || !state.item ? (
        <>
          <ResaleTitleSkeleton />
          <ExhibitionContentSkeleton />
          <ExhibitionPeriodSkeleton />
          <TicketNumberSkeletonCell />
        </>
      ) : (
        <>
          <ResaleTitleCell />
          {state.item.resaleEnabled && !state.isResaleAvailable && <Notice />}
          <ExhibitionContent
            title={state.item.title}
            skuName={state.item.sku?.unitName}
            metadata={state.item.metadata}
            price={price}
            unusedCount={state.unusedCount}
            isResaleAvailable={
              !!state.item.resaleEnabled && !!state.isResaleAvailable
            }
            formattedPrice={formattedPrice ?? '0'}
          />
          <ExhibitionPeriod metadata={state.item.metadata} />
          {state.item.resaleEnabled && state.isResaleAvailable && (
            <TicketQuantityForm
              item={state.item}
              serials={state.serials}
              onSubmit={value =>
                router.push({
                  pathname: generatePath(routerPages.ResaleCreatePrecaution, {
                    itemId: state.itemId,
                  }),
                  search: `?serialIds=${value.toString()}${
                    state.skuId ? `&skuId=${state.skuId}` : ''
                  }`,
                })
              }
            />
          )}
        </>
      )}
    </BaseLayout>
  )
}

const ResaleTitleCell: Cell = () => {
  const { t } = useTranslation('resaleCreate')
  return (
    <CellFrame sx={{ py: 5 }}>
      <Typography variant="h4">{t('resaleInfo')}</Typography>
    </CellFrame>
  )
}

const ResaleTitleSkeleton: Cell = () => (
  <CellFrame>
    <SkeletonText variant="h4" width="50%" />
    <Stack sx={{ pt: 8 }}>
      <Stack sx={{ bgcolor: 'grey.50' }}>
        <Skeleton
          variant="rectangular"
          height={20}
          width="100%"
          sx={{ bgcolor: 'grey.100', my: '6px' }}
        />
      </Stack>
      <SkeletonText variant="h4" />
    </Stack>
  </CellFrame>
)

const Notice: Cell = () => {
  const { t } = useTranslation('resaleCreate')
  return (
    <CellFrame
      sx={{
        p: 2,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'info.main',
        mb: 7,
      }}
    >
      <Typography variant="body2" sx={{ color: 'info.main' }}>
        {t('resaleNote')}
      </Typography>
    </CellFrame>
  )
}

const TicketNumberSkeletonCell: Cell = () => (
  <CellFrame>
    <SkeletonText variant="h4" />
    <SkeletonText variant="small" />
    <Stack direction="row" alignItems="center" spacing={2} sx={{ pt: 4 }}>
      <Skeleton variant="rectangular" width="50%" height={38} />
      <SkeletonText variant="caption" width="25%" />
    </Stack>
    <Stack alignItems="center" sx={{ pt: 9, pb: 8 }}>
      <Skeleton
        variant="rectangular"
        width={212}
        height={44}
        sx={{ borderRadius: 34 }}
      />
    </Stack>
  </CellFrame>
)
